import { Etiqueta } from "@/components/ui/etiqueta";
import { Progress } from "@/components/ui/progress";
import { ETIQUETA_ESTADO_DE_REDACCION, type CorridaDeLaRedaccion, type EstadoDeRedaccion } from "@/lib/redaccion";

interface ProgresoDeLaRedaccionProps {
  corrida: CorridaDeLaRedaccion;
}

const TONO_DEL_ESTADO: Record<EstadoDeRedaccion, "tenue" | "neutral" | "warning" | "info" | "success" | "danger"> = {
  pendiente: "tenue",
  generando: "neutral",
  propuesta: "warning",
  editada: "info",
  validada: "success",
  error: "danger",
};

/**
 * La redacción corriendo: el nivel de dependencias en que va, cuántas secciones
 * quedaron listas y el estado de cada una. Todo se lee de la corrida; el
 * servidor avanza por niveles, así que el porcentaje salta.
 *
 * Vive dentro de la tarjeta del árbol (angosta), así que es un bloque plano y no
 * otra tarjeta. No hay tiempo estimado: el servidor no lo conoce y uno inventado
 * sería una promesa. Lo que sí se dice es que se puede salir, porque corre en la
 * cola.
 */
export function ProgresoDeLaRedaccion({ corrida }: ProgresoDeLaRedaccionProps) {
  const { porcentaje, nivel, contadores, secciones } = corrida;

  return (
    <div className="bg-superficie-tenue border-border flex flex-col gap-2.5 rounded-[0.875rem] border-[1.5px] px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-ink text-[0.8125rem] font-extrabold">Redactando las secciones</span>
        <span className="text-ink font-mono text-xs font-bold">{porcentaje} %</span>
      </div>

      <Progress value={porcentaje} aria-label="Avance de la redacción" />

      <p
        className="text-ink-secundario flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold"
        aria-live="polite"
      >
        <span className="flex items-center gap-2">
          <span aria-hidden className="bg-primary size-[0.4375rem] shrink-0 animate-pulse rounded-full" />
          <span className="font-mono">
            Nivel {nivel.actual} de {nivel.total}
          </span>
        </span>
        <span>
          {contadores.redactadas} de {contadores.secciones} secciones redactadas
        </span>
      </p>

      {secciones.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {secciones.map((s) => (
            <li key={s.redaccion_id} className="flex items-center justify-between gap-3 text-xs font-semibold">
              <span className="text-ink-secundario min-w-0 truncate">{s.nombre}</span>
              {s.estado ? (
                <Etiqueta size="sm" tono={TONO_DEL_ESTADO[s.estado]}>
                  {ETIQUETA_ESTADO_DE_REDACCION[s.estado]}
                </Etiqueta>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-ink-secundario text-xs font-semibold text-pretty">
        Puedes cerrar esta página. La redacción sigue en la cola; al volver verás los textos listos para revisar.
      </p>
    </div>
  );
}
